import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import moment from 'moment';

import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { ButtonSize } from 'packages/kakapo-ui-kit/types/Button';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import { useMixpanel } from 'packages/kakapo-ui-application/hooks/useMixpanel';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';

import PrimaryButton from '../buttons/PrimaryButton';
import { ParagraphSmall } from '../common/Paragraph';
import HorizontalRule from '../common/HorizontalRule';

import CycleMomentStatus from './CycleMomentStatus';
import { Button } from '@treams/treams-ui';

const MomentTitle = styled(Box)`
	color: ${(props) => props.theme.colors.slateBlue};
	font-weight: bold;
	font-size: 16px;
`;

const CycleTitle = styled.div`
	color: ${(props) => props.theme.colors.slateBlue};
`;

const ComingSoon = styled.div`
	color: ${(props) => props.theme.colors.slateBlue};
	text-align: center;
`;

const Skip = styled(Box)`
	color: ${(props) => props.theme.colors.slateBlue};
	cursor: pointer;
	text-decoration: underline;
	font-size: 12px;

	&:hover {
		text-decoration: none;

		&:active {
			color: black;
		}
	}
`;

function CycleMomentActionButton(props: {
	showInstruction?: (options: {
		title: string;
		instruction?: graphql.LocalizedAttributedString;
		buttonTitle: string;
		callback: () => void;
	}) => void;
	template: graphql.MomentTemplate;
	onClick: () => void;
	to?: string;
	size: ButtonSize;
	children: string;
}) {
	const { localize } = useLocalize();
	const history = useHistory();

	return (
		<>
			<PrimaryButton
				onClick={() => {
					if (props.template.description && props.showInstruction) {
						props.showInstruction({
							title: localize(props.template.title).text,
							instruction: props.template.description ?? undefined,
							buttonTitle: props.children,
							callback: () => {
								props.onClick();
								if (props.to) {
									history.push(props.to);
								}
							},
						});
					} else {
						props.onClick();
						if (props.to) {
							history.push(props.to);
						}
					}
				}}
				size={ButtonSize.Small}
			>
				{props.children}
			</PrimaryButton>
		</>
	);
}

function CycleMomentAction({
	instance,
	showInstruction,
}: {
	instance: graphql.MomentInstance;
	showInstruction?: (
		options:
			| {
					title: string;
					instruction?: graphql.LocalizedAttributedString;
					buttonTitle: string;
					callback: () => void;
			  }
			| undefined,
	) => void;
}) {
	const { t } = useTranslation();

	const authenticatedUser = useAuthenticatedUser();
	const mixpanel = useMixpanel();

	const isViewer = instance.user.id === authenticatedUser.id;

	const [completeMomentInstance] = graphql.useCompleteMomentInstanceTaskMutation();

	if (instance.evaluation) {
		return (
			<CycleMomentActionButton
				template={instance.momentTemplate}
				onClick={() => mixpanel?.track('moments.evaluations.view')}
				to={`/user/${instance.user.id}/evaluations-v2/${instance.evaluation.id}`}
				size={ButtonSize.Small}
			>
				{t('inbox.cycles.moments.actions.evaluations.view')}
			</CycleMomentActionButton>
		);
	}

	if (instance.momentTemplate.type === graphql.MomentType.Evaluation && isViewer) {
		return (
			<CycleMomentActionButton
				template={instance.momentTemplate}
				onClick={() => mixpanel?.track('moments.evaluations.start')}
				to={`/user/${instance.user.id}/evaluations-v2/request-from-moment/${instance.id}`}
				size={ButtonSize.Small}
				showInstruction={showInstruction}
			>
				{t('inbox.cycles.moments.actions.evaluations.start')}
			</CycleMomentActionButton>
		);
	}

	if (
		instance.momentTemplate.type === graphql.MomentType.Feedback &&
		instance.status === graphql.MomentStatus.Open &&
		isViewer
	) {
		return (
			<CycleMomentActionButton
				template={instance.momentTemplate}
				onClick={() => mixpanel?.track('moments.feedback.start')}
				to={`/user/${instance.user.id}/feedback-v2/request-from-moment/${instance.id}`}
				size={ButtonSize.Small}
				showInstruction={showInstruction}
			>
				{t('inbox.cycles.moments.actions.feedback.start')}
			</CycleMomentActionButton>
		);
	}

	if (
		instance.momentTemplate.type === graphql.MomentType.Goals &&
		instance.status === graphql.MomentStatus.Open &&
		isViewer
	) {
		return (
			<CycleMomentActionButton
				template={instance.momentTemplate}
				onClick={() => mixpanel?.track('moments.goals.start')}
				to={`/user/${instance.user.id}/goals/create-from-moment/${instance.id}`}
				size={ButtonSize.Small}
				showInstruction={showInstruction}
			>
				{t('inbox.cycles.moments.actions.goals.start')}
			</CycleMomentActionButton>
		);
	}

	if (
		instance.momentTemplate.type === graphql.MomentType.Message &&
		instance.status === graphql.MomentStatus.Open &&
		isViewer
	) {
		return (
			<CycleMomentActionButton
				template={instance.momentTemplate}
				onClick={async () => {
					mixpanel?.track('moments.message.start');
					await completeMomentInstance({
						variables: {
							input: {
								id: instance.id,
							},
						},
					});
					showInstruction?.(undefined);
				}}
				size={ButtonSize.Small}
				showInstruction={showInstruction}
			>
				{t('inbox.cycles.moments.actions.message.start')}
			</CycleMomentActionButton>
		);
	}

	return null;
}

export default function CycleMomentPopover({
	cycle,
	instance,
	template,
	startDate,
	endDate,
	onSkip,
	onSendReminder,
	showInstruction,
}: {
	cycle: graphql.CycleTemplate;
	template: graphql.MomentTemplate;
	instance?: graphql.MomentInstance;
	startDate: Date;
	endDate: Date;
	onSkip?: () => void;
	onSendReminder?: () => void;
	showInstruction: (
		options:
			| {
					title: string;
					instruction?: graphql.LocalizedAttributedString;
					buttonTitle: string;
					callback: () => void;
			  }
			| undefined,
	) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const { isWorkspaceSuperuser } = useViewerRoles();

	return (
		<Box p={3}>
			<Box mb={3}>
				<Flex justifyContent="space-between">
					<MomentTitle>{localize(template.title).text}</MomentTitle>
				</Flex>
				<Box mb={2}>
					<ParagraphSmall color="lightBlueDark">
						{moment.utc(startDate).format('DD MMMM YYYY')} &mdash;{' '}
						{moment.utc(endDate).format('DD MMMM YYYY')}
					</ParagraphSmall>
				</Box>
				<CycleTitle>
					{t('inbox.cycles.moments.cycle.label', { title: localize(cycle.title).text })}
				</CycleTitle>

				<Box mt={3}>
					<HorizontalRule />
				</Box>
			</Box>
			{instance ? (
				<>
					{instance.status === graphql.MomentStatus.Skipped ? (
						<ComingSoon>
							{t('inbox.cycles.moments.skipped.reason')}
							<br />
							{instance.skipReason === graphql.MomentSkipReason.Other
								? instance.skipReasonOther
								: t(`inbox.cycle.moments.skipModal.reason.${instance.skipReason!.toLowerCase()}`)}
						</ComingSoon>
					) : (
						<Box mb={2}>
							<CycleMomentStatus instance={instance} />
						</Box>
					)}
					<Flex alignItems="center" flexDirection="column">
						{instance.status !== graphql.MomentStatus.Skipped && (
							<CycleMomentAction instance={instance} showInstruction={showInstruction} />
						)}
						{instance.status === graphql.MomentStatus.Open && onSendReminder && (
							<Button
								title={t('inbox.cycle.moments.sendReminderButton.title')}
								small
								onClick={onSendReminder}
							/>
						)}

						{instance.status === graphql.MomentStatus.Open && isWorkspaceSuperuser && onSkip && (
							<Box mt={2}>
								<Skip onClick={onSkip}>{t('inbox.cycle.moments.skipButton.title')}</Skip>
							</Box>
						)}
					</Flex>
				</>
			) : isWorkspaceSuperuser && onSkip ? (
				<Flex alignItems="center" flexDirection="column">
					<Box mt={2}>
						<Skip onClick={onSkip}>{t('inbox.cycle.moments.skipButton.title')}</Skip>
					</Box>
				</Flex>
			) : (
				<ComingSoon>{t('inbox.cycles.moments.noActionRequired')}</ComingSoon>
			)}
		</Box>
	);
}
